/**
 * دکمه‌ی شهر، جلوی کادرِ جستجو — قرینه‌ی
 * apps/mobile/src/features/discover/CityMenu.tsx.
 *
 * سه کار می‌کنه، به همین ترتیبِ اهمیت:
 *
 *   ۱. **می‌گه الان کدوم شهری.** تا دیروز این جواب هیچ‌جای صفحه نبود و
 *      فهرست فقط «رخدادها» بود — رخدادهای کجا؟
 *   ۲. با یه کلیک می‌شه عوضش کرد؛ فقط بینِ شهرهایی که محتوا دارن. انتخاب
 *      روی همین دستگاه **می‌مونه** تا خودِ کاربر برش گردونه — دیفالتِ
 *      حساب دست نمی‌خوره (نگاه کن به core/lib/cityScope).
 *   ۳. و اگه شهرِ آدم توی فهرست نبود، همون‌جا می‌شه درخواستش داد.
 *
 * فهرست از یه حدی به بعد خودش دراز می‌شه — هر شهری که کرالر توش رخدادی
 * منتشر کنه خودکار اضافه می‌شه (مایگریشن 0065) — پس بالای فهرست یه کادرِ
 * جستجوست که **اسمِ کشور و استان رو هم می‌گرده**: کسی که نمی‌دونه ما کدوم
 * شهرها رو داریم از «Canada» شروع می‌کنه نه از اسمِ شهر. منطقش توی
 * core/lib/cityRank.ts (`filterAppCities`) است تا تست‌پذیر باشه.
 *
 * وقتی شهرِ غیرِ دیفالت رو می‌بینی، دو نشونه داری: نقطه‌ی روی دکمه، و
 * ردیفِ «برگرد به {شهرِ خودم}» زیرِ فهرست — که تنها راهِ برگشتنه، کنارِ
 * انتخابِ دوباره‌ی خودِ دیفالت. هیچ برگشتِ خودکاری در کار نیست.
 */
import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cityRepo } from '@/core/data/cityRepo';
import { useSession } from '@/core/session';
import { activeCity, hasTempCity, homeCity, setTempCity, useCityScope } from '@/core/lib/cityScope';
import { filterAppCities } from '@/core/lib/cityRank';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { PinIcon } from '@/ui/icons';
import { useOutsideClose } from '@/ui/useOutsideClose';
import { WhereYouLive, useWhereYouLive } from '@/features/profile/WhereYouLive';

export function CityMenu() {
  useLocale();
  useCityScope();
  const { profile } = useSession();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  useOutsideClose(wrap, open, () => setOpen(false));

  const place = useWhereYouLive();
  const current = activeCity();
  const home = homeCity();

  // فهرست تا وقتی کادر باز نشده گرفته نمی‌شه: صفحه‌ی اول برای همه باز می‌شه
  // و این یه کوئریِ اضافه‌ست که فقط کسی که واقعاً شهر رو زده لازمش داره.
  const cities = useQuery({ queryKey: ['appCities'], queryFn: cityRepo.list, enabled: open });

  const shown = filterAppCities(cities.data ?? [], q);

  const request = useMutation({
    mutationFn: () => cityRepo.request(place.cityId!, profile?.id ?? null),
    onSuccess: () => {
      setSent(place.city);
      setAsking(false);
      place.setCity('', null);
    },
    onError: (e) => { reportError('web.city.request', e); setSent(null); },
  });

  return (
    <div className="popover-wrap" ref={wrap}>
      <button type="button" className={`city-btn${open ? ' on' : ''}`}
        aria-haspopup="menu" aria-expanded={open} data-testid="city-button"
        onClick={() => { setQ(''); setOpen((v) => !v); }}>
        <PinIcon />
        <span className="city-btn-name">{current?.name ?? t('cityLabel')}</span>
        {hasTempCity() ? <span className="navdot" aria-hidden="true" /> : null}
      </button>

      {open ? (
        <div className="menu popover-menu city-menu" role="menu">
          <p className="menu-title">{t('cityPickerTitle')}</p>
          {/* autoFocus چون منو تازه با کلیک باز شده و کیبورد روی وب هزینه‌ای
              نداره — می‌شه بدونِ یه کلیکِ اضافه شروع به تایپ کرد. */}
          <input className="input city-search" data-testid="city-search"
            placeholder={t('citySearchHint')} value={q} autoFocus
            onChange={(e) => setQ(e.target.value)} />

          {shown.map((c) => (
            <button key={c.id} type="button" role="menuitem"
              className={`menu-item${c.id === current?.id ? ' active' : ''}`}
              onClick={() => { setTempCity(c); setOpen(false); }}>
              {c.name}
              {c.region ? <small>{c.region}</small> : null}
            </button>
          ))}
          {cities.isLoading ? <p className="dim menu-note">…</p> : null}
          {/* هیچی نخوند: ردیفِ «شهرم این‌جا نیست» همین پایینه و دقیقاً همین‌جا
              به کار می‌آد. */}
          {!cities.isLoading && q.trim() && shown.length === 0
            ? <p className="dim menu-note">{t('searchNoMatch')}</p> : null}

          {/* راهِ برگشت، فقط وقتی جایی برای برگشتن هست. خودش هم توضیحه هم
              دکمه: می‌گه شهرِ خودت کدومه و با یه کلیک برت می‌گردونه. */}
          {hasTempCity() && home && !q.trim() ? (
            <button type="button" role="menuitem" className="menu-item dimmed"
              onClick={() => { setTempCity(null); setOpen(false); }}>
              {t('cityBackHome', { city: home.name })}
            </button>
          ) : null}

          {asking ? (
            <div className="menu-form">
              <WhereYouLive state={place} />
              {/* اسمِ این دکمه با اون ردیفی که فرم رو باز می‌کنه فرق داره و
                  باید هم فرق کنه: اون یکی یه شکایته («شهرم این‌جا نیست») و
                  این یکی یه کار. */}
              <button type="button" className="btn block"
                disabled={!place.cityId || request.isPending}
                onClick={() => request.mutate()}>
                {request.isPending ? t('saving') : t('cityRequestSubmit')}
              </button>
            </div>
          ) : (
            <button type="button" role="menuitem" className="menu-item dimmed"
              onClick={() => { setAsking(true); setSent(null); }}>
              {t('cityRequestCta')}
            </button>
          )}

          {sent ? <p className="dim menu-note">{t('cityRequestSent', { city: sent })}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
